import { useState } from 'react'
import { sanitizeHtml } from '../../utils/richText'

/**
 * Componente para previsualizar archivos adjuntos.
 * Renderiza una miniatura si el archivo es una imagen o un "chip" informativo si es un documento.
 * * @component
 * @param {Object} props - Propiedades del componente.
 * @param {Object} props.file - Objeto con la información del archivo.
 * @param {string} props.file.url - URL de acceso al archivo.
 * @param {string} props.file.name - Nombre del archivo.
 * @param {string} [props.file.mimeType] - Tipo MIME del archivo (ej: 'image/jpeg', 'application/pdf').
 * @param {number} [props.file.size] - Tamaño del archivo en bytes.
 * @returns {JSX.Element} Un enlace con la previsualización del archivo.
 */
const FilePreview = ({ file }) => {
  const isImage = file.mimeType?.startsWith('image/')

  /**
   * Formatea el tamaño del archivo de bytes a KB o MB.
   * @param {number} bytes - Tamaño en bytes.
   * @returns {string} Tamaño formateado (ej: "1.5 MB").
   */
  const formatSize = (bytes) => {
    if (!bytes) return ''
    return bytes < 1024 * 1024
      ? `${(bytes / 1024).toFixed(0)} KB`
      : `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  if (isImage) {
    return (
      <a
        href={file.url}
        target="_blank"
        rel="noopener noreferrer"
        className="file-thumb-link"
      >
        <img
          src={file.url}
          alt={file.name}
          className="file-thumb rounded-2"
          title={file.name}
        />
      </a>
    )
  }

  return (
    <a
      href={file.url}
      target="_blank"
      rel="noopener noreferrer"
      className="file-doc-chip d-flex align-items-center gap-1 rounded-2 px-2 py-1 text-decoration-none"
      title={file.name}
    >
      <i className="fa-solid fa-file-lines small"></i>
      <span
        className="text-truncate"
        style={{ maxWidth: 120, fontSize: '0.72rem' }}
      >
        {file.name}
      </span>
      <span
        className="text-muted"
        style={{ fontSize: '0.65rem', whiteSpace: 'nowrap' }}
      >
        {formatSize(file.size)}
      </span>
    </a>
  )
}

/**
 * Tarjeta informativa que muestra los detalles de una solución técnica.
 * Incluye título, descripción, fecha, archivos adjuntos y lógica de borrado con confirmación.
 * * @component
 * @param {Object} props - Propiedades del componente.
 * @param {Object} props.data - Objeto con los datos de la solución.
 * @param {string|number} props.data.id - Identificador único de la solución.
 * @param {string} props.data.title - Título de la solución.
 * @param {string} props.data.description - Procedimiento detallado de la solución.
 * @param {string} [props.data.createdAt] - Fecha de creación (ISO String o Timestamp).
 * @param {string|number} [props.data.categoryId] - ID de la categoría asociada.
 * @param {Array<Object>} [props.data.files] - Lista de archivos asociados (ver estructura en FilePreview).
 * @param {Function} props.onDelete - Callback ejecutado al confirmar el borrado. Recibe el ID de la solución.
 * @returns {JSX.Element} Tarjeta de solución con estilos responsivos.
 */
const CardSolution = ({ data, onDelete }) => {
  const [showConfirm, setShowConfirm] = useState(false)

  /**
   * Formatea una fecha para visualización en español.
   * @param {string|Date} date - Fecha a formatear.
   * @returns {string} Fecha formateada (ej: "27 abr.").
   */
  const formatDate = (date) => {
    if (!date) return ''
    return new Date(date).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
    })
  }

  const hasFiles = data.files?.length > 0

  return (
    <div className="card h-100 border-0 shadow-sm custom-solution-card mb-3">
      <div className="card-body d-flex gap-3 p-3">
        {/* Icono */}
        <div className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0 solution-icon">
          <i className="fa-solid fa-lightbulb fs-5"></i>
        </div>

        {/* Contenido */}
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <h6 className="fw-bold mb-0 text-truncate pe-2">{data.title}</h6>
            <span
              className="small text-muted text-uppercase fw-semibold"
              style={{ fontSize: '0.7rem' }}
            >
              {formatDate(data.createdAt)}
            </span>
          </div>

          <div
            className="small text-secondary mb-2 solution-rich-text"
            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(data.description || ''),
            }}
          />

          {/* Archivos adjuntos */}
          {hasFiles && (
            <div className="mb-2">
              <p className="text-muted mb-1" style={{ fontSize: '0.7rem' }}>
                <i className="fa-solid fa-paperclip me-1"></i>
                {data.files.length} archivo{data.files.length > 1 ? 's' : ''}{' '}
                adjunto{data.files.length > 1 ? 's' : ''}
              </p>
              <div className="d-flex flex-wrap gap-2">
                {data.files.map((file) => (
                  <FilePreview key={file.id} file={file} />
                ))}
              </div>
            </div>
          )}

          <div className="d-flex align-items-center justify-content-between">
            <span className="badge rounded-pill bg-primary-subtle text-primary border border-primary-subtle px-2 py-1">
              <i className="fa-solid fa-tag me-1"></i>
              ID: {data.categoryId || 'General'}
            </span>

            {/* Lógica de Confirmación de Borrado */}
            <div className="d-flex align-items-center gap-1">
              {!showConfirm ? (
                <button
                  className="btn btn-sm btn-outline-danger border-0 rounded-circle"
                  onClick={() => setShowConfirm(true)}
                  title="Borrar respuesta"
                >
                  <i className="fa-solid fa-trash-can"></i>
                </button>
              ) : (
                <div className="d-flex gap-2 animate-fade-in">
                  <button
                    className="btn btn-sm btn-danger fw-bold py-0 px-2"
                    style={{ fontSize: '0.7rem' }}
                    onClick={() => {
                      onDelete(data.id)
                      setShowConfirm(false)
                    }}
                  >
                    Confirmar
                  </button>
                  <button
                    className="btn btn-sm btn-light fw-bold py-0 px-2 border"
                    style={{ fontSize: '0.7rem' }}
                    onClick={() => setShowConfirm(false)}
                  >
                    Cancelar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* Los colores salen de los tokens del Design System: el modo oscuro
           sale solo, sin un bloque aparte por cada regla. */
        .custom-solution-card {
          transition: transform 0.2s, box-shadow 0.2s;
          background: var(--bg-surface);
          border: 1px solid var(--border) !important;
        }
        .solution-rich-text ul,
        .solution-rich-text ol {
          margin: 0 0 0 1.1rem;
          padding-left: 0.4rem;
        }
        .solution-rich-text p { margin: 0 0 0.35rem; }
        .solution-rich-text :last-child { margin-bottom: 0; }
        .custom-solution-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 15px rgba(0,0,0,0.05) !important;
        }
        .solution-icon {
          width: 42px;
          height: 42px;
          background: var(--gm-primary-soft);
          color: var(--brand);
        }
        .file-thumb {
          width: 64px;
          height: 64px;
          object-fit: cover;
          border: 1px solid var(--border);
          transition: opacity 0.2s;
        }
        .file-thumb-link:hover .file-thumb {
          opacity: 0.85;
        }
        .file-doc-chip {
          background: var(--bg-surface-2);
          border: 1px solid var(--border);
          color: var(--text-2);
          font-size: 0.72rem;
          transition: background 0.15s;
        }
        .file-doc-chip:hover {
          background: var(--bg-surface-3);
          color: var(--text);
        }
        .animate-fade-in {
          animation: fadeIn 0.2s ease-in-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateX(5px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  )
}

export default CardSolution
